import { skipToken, useQuery, useMutation } from '@tanstack/react-query';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type {
  CreateSupplierPaymentDetailsInput,
  UpdateSupplierPaymentDetailsInput,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export function useSupplierPaymentDetails(supplierId: string | undefined) {
  const query = useQuery({
    ...orpc.accountsPayable.supplierPaymentDetails.list.queryOptions({
      input: supplierId ? { supplierId } : skipToken,
    }),
    enabled: Boolean(supplierId),
  });

  return {
    ...query,
    paymentDetails: query.data?.paymentDetails ?? [],
  };
}

// ---------------------------------------------------------------------------
// Get
// ---------------------------------------------------------------------------

export function useSupplierPaymentDetail(id: string | undefined) {
  return useQuery({
    ...orpc.accountsPayable.supplierPaymentDetails.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id),
  });
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export function useCreateSupplierPaymentDetails(options?: {
  onSuccess?: () => void;
}) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.accountsPayable.supplierPaymentDetails.list.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: CreateSupplierPaymentDetailsInput) =>
      rpcClient.accountsPayable.supplierPaymentDetails.create(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Payment details saved successfully',
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to save payment details',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export function useUpdateSupplierPaymentDetails(options?: {
  onSuccess?: () => void;
}) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.accountsPayable.supplierPaymentDetails.list.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: UpdateSupplierPaymentDetailsInput) =>
      rpcClient.accountsPayable.supplierPaymentDetails.update(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Payment details updated successfully',
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update payment details',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export function useDeleteSupplierPaymentDetails(options?: {
  onSuccess?: () => void;
}) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.accountsPayable.supplierPaymentDetails.list.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: { id: string }) =>
      rpcClient.accountsPayable.supplierPaymentDetails.delete(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Payment details deleted successfully',
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to delete payment details',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
